/**
 * The languages chat can be translated between, and how to guess which one a
 * message is written in without asking anybody.
 *
 * The list is deliberately the intersection of what the free engines behind
 * `TRANSLATE_URL` ship models for and what a workspace plausibly writes in.
 * Offering a language the engine cannot serve is worse than not offering it:
 * the picker promises a translation that never arrives, and the person who
 * chose it has no way to tell that from a message nobody translated.
 */
export const TRANSLATION_LANGUAGES = [
    { code: 'ar', label: 'Arabic', native: 'العربية' },
    { code: 'bn', label: 'Bengali', native: 'বাংলা' },
    { code: 'zh', label: 'Chinese', native: '中文' },
    { code: 'cs', label: 'Czech', native: 'Čeština' },
    { code: 'nl', label: 'Dutch', native: 'Nederlands' },
    { code: 'en', label: 'English', native: 'English' },
    { code: 'fr', label: 'French', native: 'Français' },
    { code: 'de', label: 'German', native: 'Deutsch' },
    { code: 'el', label: 'Greek', native: 'Ελληνικά' },
    { code: 'he', label: 'Hebrew', native: 'עברית' },
    { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
    { code: 'id', label: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'it', label: 'Italian', native: 'Italiano' },
    { code: 'ja', label: 'Japanese', native: '日本語' },
    { code: 'ko', label: 'Korean', native: '한국어' },
    { code: 'fa', label: 'Persian', native: 'فارسی' },
    { code: 'pl', label: 'Polish', native: 'Polski' },
    { code: 'pt', label: 'Portuguese', native: 'Português' },
    { code: 'ru', label: 'Russian', native: 'Русский' },
    { code: 'es', label: 'Spanish', native: 'Español' },
    { code: 'sv', label: 'Swedish', native: 'Svenska' },
    { code: 'th', label: 'Thai', native: 'ไทย' },
    { code: 'tr', label: 'Turkish', native: 'Türkçe' },
    { code: 'uk', label: 'Ukrainian', native: 'Українська' },
    { code: 'ur', label: 'Urdu', native: 'اردو' },
    { code: 'vi', label: 'Vietnamese', native: 'Tiếng Việt' },
];
const BY_CODE = new Map(TRANSLATION_LANGUAGES.map((language) => [language.code, language]));
export const TRANSLATION_LANGUAGE_CODES = TRANSLATION_LANGUAGES.map((language) => language.code);
export function isTranslationLanguage(code) {
    return BY_CODE.has(code);
}
/** The name to print for a code, falling back to the code itself. */
export function languageName(code) {
    return BY_CODE.get(code)?.label ?? code.toUpperCase();
}
export function languageNative(code) {
    return BY_CODE.get(code)?.native ?? code.toUpperCase();
}
/**
 * Narrows a browser or device tag — `ar-SA`, `pt_BR`, `zh-Hans-CN` — to a code
 * this list knows, or `''` when it knows none.
 *
 * Region is dropped rather than matched. The engines translate a language,
 * not a dialect, and a member whose phone is set to `en-AU` wants English,
 * not nothing.
 */
export function normalizeLanguage(tag) {
    const base = (tag ?? '').trim().toLowerCase().replace('_', '-').split('-')[0];
    return BY_CODE.has(base) ? base : '';
}
/**
 * Which language a body is written in, decided from its script alone.
 *
 * This is not a language detector and is not trying to be one — it exists to
 * answer one cheap question before a message leaves the server: is this
 * obviously not in the language the reader wants? Scripts settle that for most
 * of the world and settle nothing for the Latin ones, so anything written in
 * Latin letters comes back `''` and is handed to the engine, which detects
 * properly.
 *
 * The point of doing it here at all is that the common case in a mixed-language
 * workspace is Arabic and English side by side, and this answers that case
 * without a network call per message.
 */
const SCRIPTS = [
    { code: 'ar', pattern: /[ؠ-يٮ-ۓۺ-ۿݐ-ݿﭐ-﷿ﹰ-ﻼ]/g },
    { code: 'he', pattern: /[֐-׿]/g },
    { code: 'el', pattern: /[Ͱ-Ͽἀ-῿]/g },
    { code: 'ru', pattern: /[Ѐ-ӿ]/g },
    { code: 'hi', pattern: /[ऀ-ॿ]/g },
    { code: 'bn', pattern: /[ঀ-৿]/g },
    { code: 'th', pattern: /[฀-๿]/g },
    { code: 'ko', pattern: /[가-힯ᄀ-ᇿ]/g },
    // Kana only. Han is deliberately absent so this stays the mark that
    // separates Japanese from Chinese — see `scriptLanguage`.
    { code: 'ja', pattern: /[぀-ゟ゠-ヿ]/g },
    { code: 'zh', pattern: /[一-鿿]/g },
];
/** Latin letters, including the accented ranges. */
const LATIN = /[A-Za-zÀ-ɏ]/g;
function count(text, pattern) {
    return text.match(pattern)?.length ?? 0;
}
/**
 * A language code when the script gives one away, `''` when it does not.
 *
 * Decided by majority rather than presence: an Arabic sentence naming a
 * project keeps a few Latin letters, and treating those as evidence would
 * make every such message look bilingual.
 *
 * Japanese wins over Chinese on any kana at all, rather than by majority.
 * Japanese prose is mostly Han characters with kana between them, so a
 * majority rule would read almost every Japanese sentence as Chinese —
 * whereas Chinese text contains no kana, which makes one kana conclusive.
 */
export function scriptLanguage(text) {
    const latin = count(text, LATIN);
    let best = '';
    let bestCount = 0;
    for (const script of SCRIPTS) {
        const hits = count(text, script.pattern);
        if (script.code === 'ja' && hits > 0)
            return 'ja';
        if (hits > bestCount) {
            best = script.code;
            bestCount = hits;
        }
    }
    if (bestCount === 0)
        return '';
    return bestCount > latin ? best : '';
}
/**
 * Emoji, near enough for this purpose: the astral planes where they mostly
 * live, plus the symbol blocks and the joiners that string them together.
 *
 * Written as explicit ranges rather than `\p{Extended_Pictographic}` because
 * this module is bundled into the phone app, and Hermes does not accept
 * Unicode property escapes — a regex nothing on mobile even calls would
 * still be a syntax error in the file that reaches it.
 */
const EMOJI = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF\u2600-\u27BF]|\uFE0F|\u200D|\u20E3/g;
/**
 * Any letter, in any of the scripts above or the Latin one.
 *
 * A coarse net on purpose: this decides whether a body contains words at all,
 * and a false positive costs one wasted translation while a false negative
 * silently leaves a real message untranslated.
 */
const ANY_LETTER = /[A-Za-zÀ-ɏͰ-῿Ⰰ-퟿]/;
/**
 * Whether a body is worth handing to a translation engine at all.
 *
 * An emoji, a bare link, a "+1" or a lone number reads the same in every
 * language, and translating them spends a request to produce the same string
 * back — or worse, an engine's guess at what "ok" means in Portuguese.
 */
export function worthTranslating(body) {
    const stripped = body
        .replace(/https?:\/\/\S+/g, ' ')
        .replace(/<@[^>]+>/g, ' ')
        .replace(/:[a-z\d_+-]+:/gi, ' ')
        .replace(EMOJI, ' ')
        .trim();
    return stripped.length >= 3 && ANY_LETTER.test(stripped);
}
